import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller } from "react-hook-form";
import { useSelector } from 'react-redux';
import { toast, Bounce } from 'react-toastify';
import { useParams, useNavigate } from 'react-router-dom';

import FilterSelect from "components/Form/filterSelect";
import { genderOptions, residencyOptions } from "utils/constants";
import useUploadHook from "components/uploadHook";
import { Button } from "components/Button";
import { applyJobPublicApi, getJobDetailPublicApi, sendEmailVerificationOTP, verifyEmailOTP, resendEmailVerificationOTP } from "api/services/jobs";
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, MailOutlined, LockOutlined } from '@ant-design/icons';
import { Steps, Radio, InputNumber, Skeleton, Input } from 'antd';

// Add import for PhoneInput
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const JobApply = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jobDetail, setJobDetail] = useState({});
  const [loading, setLoading] = useState(false);
  const [nationalityOption, setNationalityOption] = useState([]);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Email verification states
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpValue, setOtpValue] = useState(['', '', '', '']);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [emailForVerification, setEmailForVerification] = useState('');
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Cooldown state for resending OTP
  const [otpResendCooldown, setOtpResendCooldown] = useState(0);

  const { nationality, nationalityLoading } = useSelector(
    (state) => state.common
  );

  const applicationSchema = yup.object().shape({
    first_name: yup.string().required("First name is required"),
    // middle_name: yup.string(),
    last_name: yup.string().required("Last name is required"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required"),
    phone: yup
      .string()
      .required("Phone number is required")
      .min(6, "Phone number is too short"),
    resume: yup.string().required("Resume is required"),

    cover_letter: yup.string(),
    residence_status: yup
      .string()
      .required("Residency status is required"),
    nationality: yup
      .number()
      .transform((value, originalValue) => {
        // Check if the value is empty or not a number
        if (originalValue === '' || originalValue === undefined || originalValue === null) {
          return undefined; // Return undefined to trigger the required validation
        }
        const num = Number(originalValue);
        return isNaN(num) ? undefined : num; // Return undefined if NaN to trigger validation
      })
      .typeError("Please select a nationality")
      .required("Nationality is required"),
    gender: yup
      .string()
      .required("Gender is required"),
  });

  const {
    uploadLoading,
    handleFile
  } = useUploadHook();

  useEffect(() => {
    let _nationality = nationality.map((item) => {
      return {
        label: item.name,
        value: item.id,
      };
    });

    setNationalityOption(_nationality);
  }, [nationality]);

  useEffect(() => {
    // Fetch job details to display job title and info
    setLoading(true);
    getJobDetailPublicApi(id, {}).then(response => {
      setJobDetail(response.data);
      if (response.data.questions && response.data.questions.length > 0) {
        // Sort questions by order property
        const sortedQuestions = [...response.data.questions].sort((a, b) => a.order - b.order);
        setQuestions(sortedQuestions);
        
        // Initialize answers object
        const initialAnswers = {};
        sortedQuestions.forEach(question => {
          if (question.question_type === 'boolean') {
            initialAnswers[question.id] = null;
          } else if (question.question_type === 'numeric') {
            initialAnswers[question.id] = '';
          } else if (question.question_type === 'single_choice') {
            initialAnswers[question.id] = null;
          }
        });
        setQuestionAnswers(initialAnswers);
      }
      setLoading(false);
    }).catch(error => {
      console.error("Error fetching job details:", error);
      setLoading(false);
    });
  }, [id]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    getValues,
    watch,
    setError,
    trigger
  } = useForm({
    resolver: yupResolver(applicationSchema),
    defaultValues: {},
  });

  const fileUploadHandler = async(e) => {
    const file = e.target.files[0];
    
    // Check file size (5MB limit)
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error('File size exceeds 5MB limit. Please upload a smaller file.', {
        position: "top-right",
        autoClose: 5000,
      });
      e.target.value = ''; // Clear the file input
      return;
    }
    
    const url = await handleFile(e);
    setValue("resume", url)
    // setValue("resume", "https://www.google.com");
  }

  const handleBackToJob = () => {
    // Use the same ID/slug format that was used to navigate here
    navigate(`/job-view/${id}`);
  };

  const handleAnswerChange = (questionId, value) => {
    setQuestionAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const validateQuestionAnswers = () => {
    // Get only visible questions (handle conditional questions)
    const visibleQuestions = questions.filter(question => {
      if (!question.is_conditional) return true;
      
      const parentQuestion = questions.find(q => q.id === question.parent_question_id);
      if (!parentQuestion) return false;
      
      const parentAnswer = questionAnswers[parentQuestion.id];
      
      if (parentQuestion.question_type === 'boolean') {
        return parentAnswer === true && question.show_when_parent_answer === "['Yes']" ||
               parentAnswer === false && question.show_when_parent_answer === "['No']";
      } else if (parentQuestion.question_type === 'single_choice') {
        return question.show_when_parent_options.includes(parentAnswer);
      }
      
      return false;
    });
    
    // Check if all required visible questions are answered
    const unansweredRequiredQuestions = visibleQuestions.filter(question => {
      if (!question.is_required) return false;
      
      const answer = questionAnswers[question.id];
      if (answer === null || answer === undefined || answer === '') return true;
      
      return false;
    });
    
    return unansweredRequiredQuestions.length === 0;
  };

  // Function to handle sending OTP
  const handleSendOTP = async () => {
    console.log("Send OTP button clicked");
    const email = getValues('email');
    console.log("Email value:", email);
    
    // Basic email validation
    if (!email) {
      setError('email', {
        type: 'manual',
        message: 'Please enter an email address'
      });
      return;
    }
    
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('email', {
        type: 'manual',
        message: 'Please enter a valid email address'
      });
      return;
    }
    
    setIsSendingOTP(true);
    setEmailForVerification(email);
    
    try {
      await sendEmailVerificationOTP({
        email: email,
        job: jobDetail.id
      });
      
      // Clear any email error when OTP is successfully sent
      setError('email', { type: '', message: '' });
      // Start the cooldown timer (30 seconds)
      setOtpResendCooldown(30);
      setShowOTPInput(true);
      toast.success('OTP sent to your email address', {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error(error?.response?.data?.error || 'Failed to send OTP. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSendingOTP(false);
    }
  };
  
  // Function to handle OTP input change
  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      value = value.slice(0, 1);
    }
    
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) {
      return;
    }
    
    const newOTP = [...otpValue];
    newOTP[index] = value;
    setOtpValue(newOTP);
    
    // Auto focus to next input
    if (value && index < 3) {
      inputRefs[index + 1].current.focus();
    }
  };
  
  // Function to handle key press in OTP input
  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValue[index] && index > 0) {
      // Move focus to previous input when backspace is pressed on empty input
      inputRefs[index - 1].current.focus();
    }
  };
  
  // Function to handle OTP verification
  const handleVerifyOTP = async () => {
    const otp = otpValue.join('');
    
    if (otp.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    setIsVerifyingOTP(true);
    
    try {
      await verifyEmailOTP({
        email: emailForVerification,
        otp: otp
      });
      
      setIsEmailVerified(true);
      // Clear any email errors when verification is successful
      setError('email', { type: '', message: '' });
      toast.success('Email verified successfully', {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(error?.response?.data?.error || 'Invalid OTP. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsVerifyingOTP(false);
    }
  };
  
  // Function to handle resending OTP
  const handleResendOTP = async () => {
    setIsSendingOTP(true);
    
    try {
      await resendEmailVerificationOTP({
        email: emailForVerification,
        job: jobDetail.id
      });
      
      // Start the cooldown timer (30 seconds)
      setOtpResendCooldown(30);
      
      toast.success('OTP resent to your email address', {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error resending OTP:', error);
      toast.error(error?.response?.data?.error || 'Failed to resend OTP. Please try again.', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsSendingOTP(false);
    }
  };

  // Cooldown timer for resend OTP
  useEffect(() => {
    let timer;
    if (otpResendCooldown > 0) {
      timer = setTimeout(() => {
        setOtpResendCooldown(otpResendCooldown - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [otpResendCooldown]);

  // Update the nextStep function to check for email verification
  const nextStep = async () => {
    if (currentStep === 0) {
      try {
        // Validate first step form
        const isValid = await trigger();
        if (isValid) {
          // Check if email is verified
          if (!isEmailVerified) {
            toast.error('Please verify your email address before proceeding', {
              position: "top-right",
              autoClose: 3000,
            });
            return;
          }
          
          // Additional validation for nationality
          const nationalityValue = getValues('nationality');
          if (nationalityValue === '' || nationalityValue === undefined || isNaN(parseInt(nationalityValue))) {
            setError('nationality', {
              type: 'manual',
              message: 'Please select a valid nationality'
            });
            return;
          }
          
          setCurrentStep(1);
          window.scrollTo(0, 0);
        } else {
          // Show a general error message
          toast.error('Please fill in all required fields correctly', {
            position: "top-right",
            autoClose: 3000,
          });
          
          // Focus on the first field with an error
          const firstErrorField = Object.keys(errors)[0];
          if (firstErrorField && document.getElementsByName(firstErrorField)[0]) {
            document.getElementsByName(firstErrorField)[0].focus();
          }
        }
      } catch (error) {
        console.error("Validation error:", error);
        toast.error('There was a problem with form validation. Please check all fields.', {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0);
  };

  const isQuestionVisible = (question) => {
    if (!question.is_conditional) return true;
    
    const parentQuestion = questions.find(q => q.id === question.parent_question_id);
    if (!parentQuestion) return false;
    
    const parentAnswer = questionAnswers[parentQuestion.id];
    
    if (parentQuestion.question_type === 'single_choice') {
      return question.show_when_parent_options.includes(parentAnswer);
    }
    
    if (parentQuestion.question_type === 'boolean') {
      if (parentAnswer === true && question.show_when_parent_answer.includes('Yes')) {
        return true;
      }
      if (parentAnswer === false && question.show_when_parent_answer.includes('No')) {
        return true;
      }
      if (question.show_when_parent_options && question.show_when_parent_options.length > 0) {
        return question.show_when_parent_options.includes(parentAnswer);
      }
    }
    
    return false;
  };

  const saveData = (data) => {
    if (currentStep === 1) {
      // Validate questions
      if (!validateQuestionAnswers()) {
        toast.error('Please answer all required questions', {
          position: "top-right",
          autoClose: 5000,
        });
        return;
      }
    }
    
    // Validate nationality is a number
    if (isNaN(parseInt(data.nationality))) {
      setError('nationality', {
        type: 'manual',
        message: 'Please select a valid nationality'
      });
      return;
    }
    
    // Validate phone number
    if (!data.phone || data.phone.length < 6) {
      setError('phone', {
        type: 'manual',
        message: 'Please enter a valid phone number'
      });
      return;
    }
    
    setSubmitting(true);
    
    // Format the data according to API requirements
    const formattedData = {
      first_name: data.first_name,
    //   middle_name: data.middle_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone, // Phone number already includes country code from the component
      cover_letter: data.cover_letter,
      resume: data.resume,
      residence_status: data.residence_status,
      gender: data.gender,
      nationality: parseInt(data.nationality), // Ensure it's a number
      job: jobDetail.id,
      answers: Object.entries(questionAnswers).map(([questionId, answer]) => ({
        question_id: parseInt(questionId),
        answer_text: answer
      }))
    };

    applyJobPublicApi(formattedData).then(response => {
      toast.success('Your job application has been received. We will get in touch with you soon!!', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
      // Navigate back to job detail page after successful submission
      setTimeout(() => {
        navigate(`/job-view/${id}`);
      }, 3000);
    }).catch(e => {
      setSubmitting(false);
      try{
        if(e.response?.data){
            Object.entries(e.response.data).forEach(([k, v]) => {
              setError(k,
                {
                  type: 'manual',
                  message: v.join(", ")
                }
              );
            });
            prevStep()
          }
      }catch(e){
        console.log(e)
      }
      
      toast.error(e?.response?.data?.error || 'There was a problem with form validation. Please check all fields.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    });
  };

  const renderQuestion = (question) => {
    if (!isQuestionVisible(question)) return null;
    
    return (
      <div className="question-item mb-4 p-3 border rounded" key={question.id}>
        <div className="d-flex align-items-start mb-2">
          <div className="question-number me-2">
            <span className="badge bg-secondary">{question.order}</span>
          </div>
          <div>
            <h5 className="mb-1">{question.text} {question.is_required && <span className="text-danger">*</span>}</h5>
            {/* {question.is_knockout && (
              <div className="knockout-badge mb-2">
                <span className="badge bg-danger">Knockout Question</span>
              </div>
            )} */}
          </div>
        </div>
        
        {question.question_type === 'boolean' && (
          <div className="boolean-options">
            <Radio.Group 
              onChange={(e) => handleAnswerChange(question.id, e.target.value === 'yes')} 
              value={questionAnswers[question.id] === null ? undefined : questionAnswers[question.id] ? 'yes' : 'no'}
            >
              <Radio value="yes">Yes</Radio>
              <Radio value="no">No</Radio>
            </Radio.Group>
          </div>
        )}
        
        {question.question_type === 'numeric' && (
          <div className="numeric-input">
            <InputNumber 
              min={0} 
              style={{ width: '100%' }} 
              value={questionAnswers[question.id]} 
              onChange={(value) => handleAnswerChange(question.id, value)} 
              placeholder="Enter a number"
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </div>
        )}
        
        {question.question_type === 'single_choice' && question.options && question.options.length > 0 && (
          <div className="single-choice-options">
            <Radio.Group 
              onChange={(e) => handleAnswerChange(question.id, e.target.value)} 
              value={questionAnswers[question.id]}
            >
              {question.options.map(option => (
                <Radio key={option.id} value={option.id}>{option.text}</Radio>
              ))}
            </Radio.Group>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="main-wrapper">
        <div className="page-wrapper job-wrapper">
          <div className="content container">
            {/* Hero Section with Job Title */}
            <div className="nandos-hero p-4 p-md-5 rounded-3 mb-4">
              <div className="row">
                <div className="col-lg-8 mx-auto text-center">
                  <div className="nandos-logo-large mb-4">
                    <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid" style={{ maxHeight: '120px' }} />
                  </div>
                  <h1 className="display-6 fw-bold mb-3 text-white">Apply for {jobDetail?.title || "Position"}</h1>
                  <p className="lead mb-0 text-white">
                    {jobDetail?.department?.name && (
                      <span className="department-badge">
                        {jobDetail.department.name}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Back to Job Button */}
            <div className="mb-4">
              <Button
                label={<><ArrowLeftOutlined /> Back to Job Details</>}
                className="btn btn-outline-secondary"
                onClick={handleBackToJob}
              />
            </div>
            
            {/* Steps - Only show if questions exist */}
            {questions && questions.length > 0 && (
              <div className="row mb-4">
                <div className="col-lg-8 mx-auto">
                  <Steps
                    current={currentStep}
                    items={[
                      {
                        title: 'Personal Information',
                        description: '',
                      },
                      {
                        title: 'Screening Questions',
                        description: '',
                      },
                    ]}
                  />
                </div>
              </div>
            )}
            
            <div className="row">
              <div className="col-lg-8 mx-auto">
                <div className="card application-form-card">
                  <div className="nandos-card-accent"></div>
                  <div className="card-body p-4 p-md-5">
                    {loading ? (
                      <div className="loading-state">
                        <Skeleton active paragraph={{ rows: 8 }} />
                      </div>
                    ) : (questions && questions.length > 0 && currentStep === 1) ? (
                      <>
                        <h3 className="mb-4">Screening Questions</h3>
                        <p className="text-muted mb-4">Please answer the following questions to complete your application.</p>
                        
                        <div className="questions-container">
                          {questions.map(question => renderQuestion(question))}
                        </div>
                        
                        <div className="submit-section row mt-4">
                          <div className="col-12 col-md-6 mb-3 mb-md-0">
                            <Button
                              onClick={prevStep}
                              label={<>
                                <ArrowLeftOutlined className="me-2" />
                                <span>Back</span>
                              </>}
                              className="btn btn-outline-secondary w-100"
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <Button
                              onClick={(e) => {
                                e.preventDefault();
                                handleSubmit(saveData)();
                              }}
                              label={submitting ? <><LoadingOutlined className="me-2" /> <span>Submitting...</span></> : "Submit Application"}
                              className="btn nandos-btn w-100"
                              disabled={submitting}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="mb-4">Application Form</h3>
                        
                        <form>
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-block mb-3">
                                <label className="col-form-label">
                                  First Name <span className="text-danger">*</span>
                                </label>
                                <input 
                                  {...register("first_name")} 
                                  className={`form-control ${errors?.first_name ? 'is-invalid' : ''}`} 
                                  type="text" 
                                  placeholder="Enter your first name" 
                                />
                                {errors?.first_name && (
                                  <div className="invalid-feedback d-block">
                                    {errors.first_name.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* <div className="col-md-4">
                              <div className="input-block mb-3">
                                <label className="col-form-label">
                                  Middle Name
                                </label>
                                <input 
                                  {...register("middle_name")} 
                                  className={`form-control ${errors?.middle_name ? 'is-invalid' : ''}`} 
                                  type="text" 
                                  placeholder="Enter your middle name (optional)" 
                                />
                                {errors?.middle_name && (
                                  <div className="invalid-feedback d-block">
                                    {errors.middle_name.message}
                                  </div>
                                )}
                              </div>
                            </div> */}
                            <div className="col-md-6">
                              <div className="input-block mb-3">
                                <label className="col-form-label">
                                  Last Name <span className="text-danger">*</span>
                                </label>
                                <input 
                                  {...register("last_name")} 
                                  className={`form-control ${errors?.last_name ? 'is-invalid' : ''}`} 
                                  type="text" 
                                  placeholder="Enter your last name" 
                                />
                                {errors?.last_name && (
                                  <div className="invalid-feedback d-block">
                                    {errors.last_name.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-block mb-3">
                                <label className="col-form-label">
                                  Phone Number <span className="text-danger">*</span>
                                </label>
                                <Controller
                                  name="phone"
                                  control={control}
                                  render={({ field: { onChange, value } }) => (
                                    <PhoneInput
                                      country={'ae'} // Default country (UAE)
                                      value={value}
                                      onChange={onChange}
                                      inputClass={`form-control ${errors?.phone ? 'is-invalid' : ''}`}
                                      containerClass="phone-input-container"
                                      buttonClass={errors?.phone ? 'phone-dropdown-error' : ''}
                                      inputProps={{
                                        name: 'phone',
                                        required: true,
                                      }}
                                    />
                                  )}
                                />
                                {errors?.phone && (
                                  <div className="invalid-feedback d-block">
                                    {errors.phone.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="input-block mb-3">
                                <label className="col-form-label">Email Address <span className="text-danger">*</span></label>
                                <div className="input-group">
                                  <input
                                    {...register("email")}
                                    className={`form-control ${errors?.email ? 'is-invalid' : ''}`}
                                    type="text"
                                    placeholder="Enter your email address"
                                    disabled={isEmailVerified}
                                    onChange={(e) => {
                                      if (isEmailVerified && e.target.value !== emailForVerification) {
                                        setIsEmailVerified(false);
                                        setShowOTPInput(false);
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={handleSendOTP}
                                    className={`btn ${isEmailVerified ? 'btn-success' : 'btn-primary'}`}
                                    disabled={isSendingOTP || isEmailVerified}
                                  >
                                    {isSendingOTP ? "Loading.." : isEmailVerified ? <span ><span style={{ marginRight: '5px' }}>✓</span>Verified</span> : "Verify"}
                                  </button>
                                </div>
                                {errors?.email && !isEmailVerified && (
                                  <div className="invalid-feedback d-block">
                                    {errors.email.message}
                                  </div>
                                )}
                              </div>
                              
                              {/* OTP Input */}
                              {showOTPInput && !isEmailVerified && (
                                <div className="otp-section mb-3">
                                  <label className="col-form-label d-flex align-items-center">
                                    <LockOutlined className="me-2" /> Enter OTP sent to your email
                                  </label>
                                  <div className="d-flex justify-content-between mb-2">
                                    {[0, 1, 2, 3].map((index) => (
                                      <input
                                        key={index}
                                        ref={inputRefs[index]}
                                        type="tel"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        className="form-control text-center otp-input"
                                        value={otpValue[index]}
                                        onChange={(e) => handleOTPChange(index, e.target.value)}
                                        onKeyDown={(e) => handleOTPKeyDown(index, e)}
                                        maxLength={1}
                                      />
                                    ))}
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <Button
                                      onClick={handleVerifyOTP}
                                      label={isVerifyingOTP ? <LoadingOutlined /> : "Verify OTP"}
                                      className="btn btn-sm btn-primary"
                                      disabled={isVerifyingOTP || otpValue.join('').length !== 4}
                                    />
                                    <div>
                                      <Button
                                        onClick={handleResendOTP}
                                        label={isSendingOTP ? "Loading.." : "Resend OTP"}
                                        className="btn btn-sm btn-link text-primary"
                                        disabled={isSendingOTP || otpResendCooldown > 0}
                                      />
                                      {otpResendCooldown > 0 && (
                                        <div className="cooldown-timer text-center mt-1">
                                          <small className="text-muted">
                                            Resend in <span className="fw-bold">{otpResendCooldown}s</span>
                                          </small>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="mt-2 small text-muted">
                                    <i className="fa fa-info-circle me-1"></i> 
                                    Please check your spam folder if you don't see the email in your inbox.
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                  
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-block mb-3">
                                <label className="col-form-label">Nationality <span className="text-danger">*</span></label>
                                <select 
                                  {...register("nationality")} 
                                  className={`form-select ${errors?.nationality ? 'is-invalid' : ''}`}
                                >
                                  <option value="">Select nationality</option>
                                  {nationalityOption.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                                {errors?.nationality && (
                                  <div className="invalid-feedback d-block">
                                    {errors.nationality.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="input-block mb-3">
                                <label className="col-form-label">Residency Status <span className="text-danger">*</span></label>
                                <select 
                                  {...register("residence_status")} 
                                  className={`form-select ${errors?.residence_status ? 'is-invalid' : ''}`}
                                >
                                  <option value="">Select residency status</option>
                                  <option value="uae citizen">Citizen</option>
                                  <option value="resident">Resident</option>
                                  <option value="dependent">Dependent</option>
                                  <option value="visit">Visit</option>
                                 
                                </select>
                                {errors?.residence_status && (
                                  <div className="invalid-feedback d-block">
                                    {errors.residence_status.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                                  
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-block mb-3">
                                <label className="col-form-label">Gender <span className="text-danger">*</span></label>
                                <select 
                                  {...register("gender")} 
                                  className={`form-select ${errors?.gender ? 'is-invalid' : ''}`}
                                >
                                  <option value="">Select gender</option>
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                </select>
                                {errors?.gender && (
                                  <div className="invalid-feedback d-block">
                                    {errors.gender.message}
                                  </div>
                                )}                              </div>
                            </div>
                            <div className="col-md-6">
                              {/* Experience field removed */}
                            </div>
                          </div>
                          
                          <div className="input-block mb-3">
                            <label className="col-form-label">Cover Letter</label>
                            <textarea
                              {...register("cover_letter")}
                              className={`form-control ${errors?.cover_letter ? 'is-invalid' : ''}`}
                              rows="5"
                              placeholder="Tell us why you're a good fit for this position"
                            ></textarea>
                            {errors?.cover_letter && (
                              <div className="invalid-feedback d-block">
                                {errors.cover_letter.message}
                              </div>
                            )}
                          </div>
                          
                          <div className="input-block mb-4">
                            <label className="col-form-label">
                              Upload your CV <span className="text-danger">*</span> {uploadLoading && <i className="fa fa-spinner fa-spin"></i>}
                            </label>
                            <input
                              type="file"
                              className={`form-control ${errors?.resume ? 'is-invalid' : ''}`}
                              id="cv_upload"
                              accept=".pdf, .docx"
                              onChange={fileUploadHandler}
                              disabled={uploadLoading}
                            />
                            <small className="form-text text-muted">Accepted formats: PDF, DOCX (Max size: 5MB)</small>
                            {errors?.resume && (
                              <div className="invalid-feedback d-block">
                                {errors.resume.message}
                              </div>
                            )}
                          </div>
                          
                          <div className="submit-section text-end">
                            {questions && questions.length > 0 ? (
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  nextStep();
                                }}
                                label="Next: Screening Questions"
                                className="btn nandos-btn btn-lg"
                                disabled={!isEmailVerified}
                              />
                            ) : (
                              <Button
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleSubmit(saveData)();
                                }}
                                label={submitting ? <><LoadingOutlined /> Submitting...</> : "Submit Application"}
                                className="btn nandos-btn btn-lg"
                                disabled={submitting || !isEmailVerified}
                              />
                            )}
                          </div>
                        </form>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="nandos-footer p-4 mt-5 text-center">
              <div className="container">
                <div className="row">
                  <div className="col-12">
                    <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid mb-3" style={{ maxHeight: '60px' }} />
                    <p className="mb-0">© {new Date().getFullYear()} Nando's. All rights reserved.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add custom CSS */}
      <style jsx>{`
        .job-wrapper {
          background-color: #f8f9fa;
          min-height: 100vh;
        }
        
        .nandos-hero {
          background: linear-gradient(135deg, #a61022 0%, #7a0c19 100%);
          color: white;
        }
        
        .application-form-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          position: relative;
          overflow: hidden;
        }
        
        .nandos-card-accent {
          height: 5px;
          background: linear-gradient(90deg, #a61022 0%, #f8c429 100%);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
        }
        
        .department-badge {
          background-color: rgba(255, 255, 255, 0.2);
          padding: 5px 15px;
          border-radius: 20px;
          font-weight: 500;
        }
        
        .nandos-btn {
          background-color: #a61022;
          border-color: #a61022;
          color: white;
        }
        
        .nandos-btn:hover {
          background-color: #7a0c19;
          border-color: #7a0c19;
          color: white;
        }
        
        .nandos-text {
          color: #a61022;
        }
        
        .nandos-footer {
          background-color: #333;
          color: white;
        }
        
        .question-item {
          background-color: #fff;
          transition: all 0.3s ease;
        }
        
        .question-item:hover {
          box-shadow: 0 5px 15px rgba(0,0,0,0.05);
        }
        
        .knockout-badge .badge {
          font-size: 0.7rem;
          font-weight: normal;
        }

        .phone-input-container {
          width: 100%;
        }
        .phone-input-container .form-control {
          width: 100% !important;
          padding-left: 50px !important;
        }
        .phone-dropdown-error {
          border-color: #dc3545 !important;
        }

        .otp-input {
          width: 60px;
          height: 60px;
          font-size: 24px;
          margin: 0 5px;
        }
        
        .cooldown-timer {
          background-color: #f8f9fa;
          border-radius: 12px;
          padding: 2px 8px;
          font-size: 0.8rem;
        }
        
        .cooldown-timer .fw-bold {
          color: #a61022;
        }
      `}</style>
    </div>
  );
};

export default JobApply;


